/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { updateUser } from "../../../api/User";
import { UserAtom } from "../../../atoms/UserAtom";
import { SpaceBetween } from "../../../styles/general";
import { Option } from "../../../types/SavedSearchTypes";
import StyledButton from "../../StyledButton";
import Switch from "../../Switch";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  width: 580px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;

  margin: 16px -24px 0px;
`;

const Wrapper = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  padding: 32px 24px;
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  border-top: 1px solid #eceeee;
  padding: 24px 24px 0px;
`;

const InputLabel = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-top: 10px;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Cancel = styled.span`
  font: normal normal 600 15px;
  position: absolute;
  cursor: pointer;
  left: 5px;
  top: 5px;
  align-items: center;
  justify-content: center;
  width: 70px;
  font-weight: 600;
  height: 30px;
  display: flex;
  background-color: #fff2e5;
  border-radius: 4px;
  color: #fb972e;
  border: 1px solid #fb972e;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
`;

const InputWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .flex-input {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
`;

const ErrorMessage = styled.div`
  color: #c41700;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  min-width: 320px;
  height: 40px;
  border: 1px solid #dddddd;
  border-radius: 5px;
  outline: none;
  font-size: 14px;
  padding: 12px;
  color: #383838;
  background-color: #ffffff;

  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;
  }

  &::placeholder {
    font-size: 14px;
    letter-spacing: 0.16px;
    color: #979797;
  }

  ${(props) =>
    props.hasError &&
    css`
      border: 1px solid #c41700;
      box-shadow: 0px 0px 6px #c417004d;
    `}

  background-color: #ffffff;

  transition: box-shadow 0.3s, border 0.3s;
`;

const SEND_CONFIRMATION_OPTIONS = [
  {
    name: "Default",
    value: "Default",
    id: "Default",
  },
  {
    name: "URL",
    value: "URL",
    id: "URL",
  },

  // {
  //   name: "Custom",
  //   value: "Custom",
  //   id: "Custom",
  // },
];

export default function EmailConfirmationConfig(): JSX.Element | null {
  const [sendConfirmationOptions, setSendConfirmationOptions] =
    useState<Option>(SEND_CONFIRMATION_OPTIONS[0]);

  const [emailConfirmationLink, setEmailConfirmationLink] = useState("");

  const [isHttps, setIsHttps] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useRecoilState(UserAtom);

  const [urlValidation, setUrlValidation] = useState("");
  const modal = useModal();

  function handleToggle(vle: boolean) {
    vle
      ? setSendConfirmationOptions(SEND_CONFIRMATION_OPTIONS[0])
      : setSendConfirmationOptions(SEND_CONFIRMATION_OPTIONS[1]);
  }

  useEffect(() => {
    if (!user) return;
    let sendconfirmationOption = SEND_CONFIRMATION_OPTIONS[0];

    if (user.options?.emailConfirmationLink) {
      sendconfirmationOption = SEND_CONFIRMATION_OPTIONS[1];
    }

    setEmailConfirmationLink(
      user.options?.emailConfirmationLink?.replace(/^https?:\/\//gi, "") || ""
    );
    setSendConfirmationOptions(sendconfirmationOption);
  }, []);

  async function handleClick() {
    setIsLoading(true);

    if (
      SEND_CONFIRMATION_OPTIONS[1].value === sendConfirmationOptions.value &&
      emailConfirmationLink.length === 0
    ) {
      setUrlValidation("The URL is required");
      setIsLoading(false);
      return;
    }

    const emailConfirmationLinkToSend =
      SEND_CONFIRMATION_OPTIONS[1].value === sendConfirmationOptions.value
        ? (isHttps ? "https://" : "http://") + emailConfirmationLink
        : undefined;

    const isUpdated = await updateUser({
      options: {
        ...user.options,
        emailConfirmationLink: emailConfirmationLinkToSend,
      },
    });

    if (isUpdated) {
      setUser((u) => ({
        ...u,
        options: {
          ...u?.options,
          emailConfirmationLink: emailConfirmationLinkToSend,
        },
      }));
      modal.popModal();
    }

    setIsLoading(false);
  }

  if (!user) return <></>;
  return (
    <ModalBody>
      <Wrapper>
        <SpaceBetween>
          <Title>Default confirmation</Title>

          <Switch
            toggled={Boolean(
              sendConfirmationOptions.value ===
                SEND_CONFIRMATION_OPTIONS[0].value
            )}
            whenToggled={handleToggle}
          />
        </SpaceBetween>

        {sendConfirmationOptions.value ===
          SEND_CONFIRMATION_OPTIONS[1].value && (
          <>
            <InputLabel>
              <Title>URL Confirmation</Title>
              <InputWrapper>
                <div className="flex-input">
                  <Input
                    hasError={Boolean(urlValidation)}
                    css={css`
                      border-radius: 0.375rem !important;
                      text-indent: 75px;
                    `}
                    placeholder={"Enter confirmation link"}
                    value={emailConfirmationLink}
                    onPaste={(e) => {
                      const textFromPaste = e.clipboardData
                        .getData("text")
                        .toString();

                      setEmailConfirmationLink(
                        textFromPaste.replace(/^https?:\/\//gi, "")
                      );

                      if (textFromPaste.includes("https://")) {
                        setIsHttps(true);
                      } else if (textFromPaste.includes("http://")) {
                        setIsHttps(false);
                      }
                    }}
                    onChange={(e) => {
                      setEmailConfirmationLink(
                        e.target.value.replace(/^https?:\/\//gi, "")
                      );
                      setUrlValidation("");
                    }}
                  />
                  <Cancel
                    onClick={() => {
                      setIsHttps(!isHttps);
                    }}
                  >
                    {isHttps ? "https://" : "http://"}
                  </Cancel>
                </div>
                {urlValidation && <ErrorMessage>{urlValidation}</ErrorMessage>}
              </InputWrapper>
            </InputLabel>
          </>
        )}
      </Wrapper>

      <Buttons>
        <StyledButton
          inverted
          onClick={() => modal.popModal()}
          css={css`
            width: 80px;
            margin-right: 12.5px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          isLoading={isLoading}
          onClick={handleClick}
          css={css`
            width: 80px;
          `}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
